/**
 * Per-table layout persistence for the DS `ProfileTable`.
 *
 * Stores a versioned snapshot of each table's user-customised layout
 * (middle-column order, hidden columns, collapsed columns, widths) in
 * `window.localStorage` under a caller-supplied key. The DS table is the
 * single source of truth for this shape — any page that wants custom
 * persistence should wrap these helpers or pass `layoutStorageKey={undefined}`
 * to keep state ephemeral.
 *
 * Moved here from `pages/dashboard/pages/profiles/utils/` so the DS table
 * no longer leaks into a feature-specific folder (see plan
 * `calm-spinning-turing.md`, Phase 1).
 */

type StringRecord = Record<string, unknown>

export interface ProfileTableLayoutState<TMiddleColumn extends string, TColumn extends string> {
    middleColumnOrder: TMiddleColumn[]
    hiddenColumns: TMiddleColumn[]
    collapsedColumns: TMiddleColumn[]
    columnWidths: Partial<Record<TColumn, number>>
}

interface PersistedProfileTableLayout<TMiddleColumn extends string, TColumn extends string> {
    version: number
    middleColumnOrder: TMiddleColumn[]
    hiddenColumns: TMiddleColumn[]
    collapsedColumns: TMiddleColumn[]
    columnWidths: Partial<Record<TColumn, number>>
}

const PROFILE_TABLE_LAYOUT_VERSION = 1

const toStringArray = <T extends string>(value: unknown): T[] => {
    if (!Array.isArray(value)) return []
    return value.filter((item): item is T => typeof item === 'string')
}

const toWidthMap = <T extends string>(value: unknown): Partial<Record<T, number>> => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return {}

    return Object.entries(value as StringRecord).reduce(
        (acc, [key, width]) => {
            if (typeof width === 'number' && Number.isFinite(width) && width > 0) {
                acc[key as T] = width
            }
            return acc
        },
        {} as Partial<Record<T, number>>
    )
}

export const readProfileTableLayout = <TMiddleColumn extends string, TColumn extends string>(
    storageKey: string
): ProfileTableLayoutState<TMiddleColumn, TColumn> | null => {
    if (typeof window === 'undefined') return null

    try {
        const raw = window.localStorage.getItem(storageKey)
        if (!raw) return null

        const parsed = JSON.parse(raw) as Partial<PersistedProfileTableLayout<TMiddleColumn, TColumn>>
        if (parsed.version !== PROFILE_TABLE_LAYOUT_VERSION) return null

        return {
            middleColumnOrder: toStringArray<TMiddleColumn>(parsed.middleColumnOrder),
            hiddenColumns: toStringArray<TMiddleColumn>(parsed.hiddenColumns),
            collapsedColumns: toStringArray<TMiddleColumn>(parsed.collapsedColumns),
            columnWidths: toWidthMap<TColumn>(parsed.columnWidths),
        }
    } catch {
        return null
    }
}

export const writeProfileTableLayout = <TMiddleColumn extends string, TColumn extends string>(
    storageKey: string,
    state: ProfileTableLayoutState<TMiddleColumn, TColumn>
) => {
    if (typeof window === 'undefined') return

    const payload: PersistedProfileTableLayout<TMiddleColumn, TColumn> = {
        version: PROFILE_TABLE_LAYOUT_VERSION,
        middleColumnOrder: state.middleColumnOrder,
        hiddenColumns: state.hiddenColumns,
        collapsedColumns: state.collapsedColumns,
        columnWidths: state.columnWidths,
    }

    try {
        window.localStorage.setItem(storageKey, JSON.stringify(payload))
    } catch {
        // Ignore localStorage write failures (quota/private mode).
    }
}
